import React from 'react'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { READINESS_OPTIONS } from './informationSystemGrcConstants'
import { fetchGrcSummaries, type GrcSummaryRow } from '../../services/informationSystemComplianceApi'
import { fetchEnabledStandards } from '../../services/informationSystemComplianceApi'
import CorrelationsPanel from './CorrelationsPanel'
import OperationalDashboardPanel from './OperationalDashboardPanel'
import BlastRadiusPanel from './BlastRadiusPanel'
import RiskComplianceSummaryRow from './RiskComplianceSummaryRow'
import RiskComplianceDetailPanel, { RiskComplianceDetailEmpty } from './RiskComplianceDetailPanel'
import RiskComplianceExecutiveWorkspace from '../enterprise-operational/RiskComplianceExecutiveWorkspace'
import EnterpriseOperationalPage from '../enterprise-operational/EnterpriseOperationalPage'
import { featureFlags } from '../../config/featureFlags'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

const LIFECYCLE_OPTIONS = ['DRAFT', 'ACTIVE', 'IN_REVIEW', 'DEPRECATED', 'ARCHIVED']
const IMPLEMENTATION_OPTIONS = ['NOT_STARTED', 'PLANNED', 'IMPLEMENTED', 'PARTIAL', 'NOT_APPLICABLE', 'NEEDS_REVIEW']

interface RiskComplianceTabProps {
  onOpenSystem?: (systemId: string) => void
  onOpenGraph?: (systemId: string) => void
}

export default function RiskComplianceTab({ onOpenSystem, onOpenGraph }: RiskComplianceTabProps) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()

  const [summaries, setSummaries] = React.useState<GrcSummaryRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [intelligenceOpen, setIntelligenceOpen] = React.useState(true)
  const [filterLifecycle, setFilterLifecycle] = React.useState('')
  const [filterStandard, setFilterStandard] = React.useState('')
  const [filterReadiness, setFilterReadiness] = React.useState('')
  const [filterImplementation, setFilterImplementation] = React.useState('')
  const [standardOptions, setStandardOptions] = React.useState<string[]>([])

  const load = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [rows, standards] = await Promise.all([
        fetchGrcSummaries(token, orgId, {
          lifecycleStatus: filterLifecycle || undefined,
          standardCode: filterStandard || undefined,
          readinessStatus: filterReadiness || undefined,
          implementationStatus: filterImplementation || undefined,
        }),
        fetchEnabledStandards(token),
      ])
      setSummaries(rows)
      setStandardOptions(standards)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.riskCompliance.error.load', 'Failed to load summaries'))
    } finally {
      setLoading(false)
    }
  }, [token, orgId, t, filterLifecycle, filterStandard, filterReadiness, filterImplementation])

  React.useEffect(() => {
    load()
  }, [load])

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return summaries
    return summaries.filter((row) => row.name.toLowerCase().includes(q))
  }, [summaries, searchQuery])

  const selectedRow = React.useMemo(
    () => (selectedId ? summaries.find((r) => r.informationSystemId === selectedId) : null),
    [summaries, selectedId]
  )

  if (loading && summaries.length === 0) {
    return <p className={styles.loadingText}>{t('common.loading')}</p>
  }

  if (featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 && summaries.length > 0) {
    return (
      <EnterpriseOperationalPage>
        <RiskComplianceExecutiveWorkspace
          summaries={summaries}
          loading={loading}
          error={error}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterLifecycle={filterLifecycle}
          setFilterLifecycle={setFilterLifecycle}
          filterStandard={filterStandard}
          setFilterStandard={setFilterStandard}
          filterReadiness={filterReadiness}
          setFilterReadiness={setFilterReadiness}
          filterImplementation={filterImplementation}
          setFilterImplementation={setFilterImplementation}
          standardOptions={standardOptions}
          onRefresh={load}
          onOpenSystem={onOpenSystem}
          onOpenGraph={onOpenGraph}
        />
      </EnterpriseOperationalPage>
    )
  }

  if (summaries.length === 0 && !error && !loading) {
    return (
      <div className={styles.riskComplianceWorkspace}>
        <p className={styles.introBanner}>
          {t(
            'assets.riskCompliance.introGrc',
            'System-based GRC projection from mapped standards, controls, evidence, and remediation.'
          )}
        </p>
        <div className={styles.emptyCard}>
          <p className={styles.emptyHint}>
            {t(
              'assets.riskCompliance.empty',
              'Map information systems to standards, controls, evidence, and remediation to see readiness here.'
            )}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.riskComplianceWorkspace}>
      <section className={styles.intelligenceSection}>
        <div className={styles.intelligenceSectionHeader}>
          <h2 className={styles.intelligenceSectionTitle}>
            {t('assets.riskCompliance.intelligenceTitle', 'Org intelligence')}
          </h2>
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => setIntelligenceOpen((v) => !v)}
            aria-expanded={intelligenceOpen}
          >
            {intelligenceOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
          </button>
        </div>
        {intelligenceOpen ? (
          <div className={styles.intelligenceGrid}>
            <OperationalDashboardPanel />
            <CorrelationsPanel />
            <BlastRadiusPanel />
          </div>
        ) : null}
      </section>

      <p className={styles.introBanner}>
        {t(
          'assets.riskCompliance.introGrc',
          'System-based GRC projection from mapped standards, controls, evidence, and remediation. Risk and compliance scores remain unavailable until Compliance Center integration.'
        )}
      </p>

      <div className={styles.rcToolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.toolbarPrimaryRow}>
            <input
              type="search"
              className={styles.searchField}
              placeholder={t('assets.riskCompliance.searchPlaceholder', 'Search systems…')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t('assets.riskCompliance.searchPlaceholder', 'Search systems')}
            />
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.lifecycle.status', 'Lifecycle')}</span>
              <select
                className={styles.selectField}
                value={filterLifecycle}
                onChange={(e) => setFilterLifecycle(e.target.value)}
              >
                <option value="">{t('assets.lifecycle.allStatuses', 'All statuses')}</option>
                {LIFECYCLE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.lifecycle.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.grc.standardCode', 'Standard')}</span>
              <select className={styles.selectField} value={filterStandard} onChange={(e) => setFilterStandard(e.target.value)}>
                <option value="">{t('assets.grc.allStandards', 'All standards')}</option>
                {standardOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.grc.readinessStatus', 'Readiness')}</span>
              <select className={styles.selectField} value={filterReadiness} onChange={(e) => setFilterReadiness(e.target.value)}>
                <option value="">{t('assets.grc.allReadiness', 'All readiness')}</option>
                {READINESS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.grc.readiness.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.grc.implementationFilter', 'Control status')}</span>
              <select
                className={styles.selectField}
                value={filterImplementation}
                onChange={(e) => setFilterImplementation(e.target.value)}
              >
                <option value="">{t('assets.grc.allImplementation', 'Any status')}</option>
                {IMPLEMENTATION_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.grc.implementation.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className={styles.toolbarRight}>
          <button type="button" className={styles.refreshBtn} onClick={() => load()}>
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {error ? (
        <p className={styles.errorBanner}>
          {error}
          <Button type="button" size="small" color="outline" onClick={() => load()}>
            {t('common.retry', 'Retry')}
          </Button>
        </p>
      ) : null}

      {filteredRows.length === 0 ? (
        <div className={styles.emptyCard}>
          <p className={styles.emptyHint}>{t('assets.riskCompliance.noSearchResults', 'No systems match your filters.')}</p>
        </div>
      ) : (
        <div className={styles.rcContentGrid}>
          <div className={styles.rcSystemsList}>
            {filteredRows.map((row) => (
              <RiskComplianceSummaryRow
                key={row.informationSystemId}
                row={row}
                selected={selectedId === row.informationSystemId}
                onSelect={() => setSelectedId(row.informationSystemId)}
                onOpenPassport={onOpenSystem ? () => onOpenSystem(row.informationSystemId) : undefined}
                onOpenGraph={onOpenGraph ? () => onOpenGraph(row.informationSystemId) : undefined}
              />
            ))}
            {selectedRow ? (
              <div className={styles.mobilePanelBelow}>
                <RiskComplianceDetailPanel
                  row={selectedRow}
                  onClose={() => setSelectedId(null)}
                  onOpenPassport={onOpenSystem ? () => onOpenSystem(selectedRow.informationSystemId) : undefined}
                  onOpenGraph={onOpenGraph ? () => onOpenGraph(selectedRow.informationSystemId) : undefined}
                />
              </div>
            ) : null}
          </div>
          <div className={styles.desktopPanelColumn}>
            {selectedRow ? (
              <RiskComplianceDetailPanel
                row={selectedRow}
                onClose={() => setSelectedId(null)}
                onOpenPassport={onOpenSystem ? () => onOpenSystem(selectedRow.informationSystemId) : undefined}
                onOpenGraph={onOpenGraph ? () => onOpenGraph(selectedRow.informationSystemId) : undefined}
              />
            ) : (
              <RiskComplianceDetailEmpty />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
